/**
 * Contract Action Model
 * Gestione delle azioni contrattuali (modifiche, integrazioni, recesso, rinnovo)
 */

import { ContractInvolvedEntity } from './contract.model';

export enum ContractActionType {
  MODIFY_DURATION = 'MODIFY_DURATION',       // Modifica Durata Contratto
  MODIFY_SUBJECT = 'MODIFY_SUBJECT',         // Modifica Oggetto Contratto
  MODIFY_ECONOMICS = 'MODIFY_ECONOMICS',               // Modifica dei Corrispettivi
  EXTEND_PARTIES = 'EXTEND_PARTIES',         // Estensione Parti Contrattuali
  RENEW_CONTRACT = 'RENEW_CONTRACT',                           // Rinnova Contratto
  TERMINATION = 'TERMINATION'                     // Recedi dal contratto
}

export enum ContractActionStatus {
  DRAFT = 'DRAFT',         // Bozza
  ACTIVE = 'ACTIVE',       // Attiva
  CANCELED = 'CANCELED'    // Annullata
}

export interface ContractAction {
  id?: number;
  contractId: number;
  actionType: ContractActionType;
  actionName: string;
  description?: string;             // Descrizione estesa
  status: ContractActionStatus;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  
  // Campi specifici per tipo azione
  newEndDate?: Date | string;      // Per MODIFY_DURATION
  newContractSubject?: string;      // Per MODIFY_SUBJECT
  newCurrency?: string;             // Per MODIFY_ECONOMICS (valuta)
  newTotalContractValue?: number;   // Per MODIFY_ECONOMICS (valore durata contratto IVA esclusa)
  newAnnualValue?: number;          // Per MODIFY_ECONOMICS (valore annuale IVA esclusa)
  newInvolvedEntities?: ContractInvolvedEntity[];   // Per EXTEND_PARTIES
  terminationReason?: string;       // Per TERMINATION
  renewalNotes?: string;            // Per RENEW_CONTRACT
  renewedContractId?: number;       // Per RENEW_CONTRACT - ID del nuovo contratto clonato post-attivazione
  
  // Template e documenti
  selectedTemplateId?: string;
  signedDocumentUrl?: string;       // URL del documento firmato caricato
  generatedDocuments?: ContractActionDocument[];
  
  // Note generiche
  notes?: string;
}

export interface ContractActionDocument {
  id?: number;
  actionId: number;
  fileName: string;
  fileUrl: string;
  documentCategory: string;
  uploadedAt?: Date | string;
}

// Helper per ottenere il label human-readable del tipo azione
export const CONTRACT_ACTION_TYPE_LABELS: Record<ContractActionType, string> = {
  [ContractActionType.MODIFY_DURATION]: 'Modifica Durata Contratto',
  [ContractActionType.MODIFY_SUBJECT]: 'Modifica Oggetto Contratto',
  [ContractActionType.MODIFY_ECONOMICS]: 'Modifica dei Corrispettivi',
  [ContractActionType.EXTEND_PARTIES]: 'Estensione Parti Contrattuali',
  [ContractActionType.RENEW_CONTRACT]: 'Rinnova Contratto',
  [ContractActionType.TERMINATION]: 'Recedi dal Contratto'
};

// Helper per ottenere il label human-readable dello stato
export const CONTRACT_ACTION_STATUS_LABELS: Record<ContractActionStatus, string> = {
  [ContractActionStatus.DRAFT]: 'BOZZA',
  [ContractActionStatus.ACTIVE]: 'ATTIVA',
  [ContractActionStatus.CANCELED]: 'ANNULLATA'
};

// Helper per ottenere la severity PrimeNG per lo stato azione
export function getActionStatusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
  switch (status) {
    case 'DRAFT':
      return 'secondary';
    case 'ACTIVE':
      return 'success';
    case 'CANCELED':
      return 'danger';
    default:
      return 'secondary';
  }
}

// Opzioni per dropdown tipo azione
export const CONTRACT_ACTION_TYPE_OPTIONS = Object.entries(CONTRACT_ACTION_TYPE_LABELS).map(([key, value]) => ({
  value: key,
  label: value
}));
